/**
 * RoomPage — Multiplayer waiting room / lobby.
 * Shows player list, ready status, AI slot filling, and game start controls.
 * Renders inside /game/:roomCode when mode=lobby.
 * Wired to Socket.io for real-time updates.
 */

import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRoomStore, useAuthStore } from '../stores';
import { emitToggleReady, emitAddAI, emitStartGame, emitLeaveRoom } from '../hooks/useSocket';
import {
  getRoomReadiness,
  isLocalPlayerHost,
  getLobbyPlayerInfos,
  getOpenSlots,
  canAddAI,
  getRoomPhaseDisplay,
  type RoomReadiness,
} from '@callbridge/ui';
import { SpadeSvg, HeartSvg, DiamondSvg, ClubSvg } from '../components/SuitIcons';

type AIDifficulty = 'easy' | 'medium' | 'hard';

export function RoomPage() {
  const { roomCode } = useParams<{ roomCode: string }>();
  const navigate = useNavigate();
  const room = useRoomStore();
  const user = useAuthStore((s) => s.user);
  const [aiDifficulty, setAiDifficulty] = useState<AIDifficulty>('medium');
  const [showAIPanel, setShowAIPanel] = useState(false);

  // Derived state from pure UI functions
  const readiness: RoomReadiness = getRoomReadiness(room);
  const isHost = isLocalPlayerHost(room);
  const playerInfos = getLobbyPlayerInfos(room);
  const openSlots = getOpenSlots(room);
  const canAdd = canAddAI(room);
  const phaseDisplay = getRoomPhaseDisplay(room);

  const handleToggleReady = useCallback(async () => {
    try {
      await emitToggleReady();
    } catch (err) {
      room.roomError(err instanceof Error ? err.message : 'Failed to toggle ready');
    }
  }, [room]);

  const handleAddAI = useCallback(async () => {
    if (!canAdd) return;
    try {
      await emitAddAI(aiDifficulty);
      setShowAIPanel(false);
    } catch (err) {
      room.roomError(err instanceof Error ? err.message : 'Failed to add AI');
    }
  }, [canAdd, aiDifficulty, room]);

  const handleStartGame = useCallback(async () => {
    if (!readiness.canStart || !isHost) return;
    try {
      await emitStartGame();
      // Server emits game:started → useSocket listener updates gameStore and roomStore
    } catch (err) {
      room.roomError(err instanceof Error ? err.message : 'Failed to start game');
    }
  }, [readiness.canStart, isHost, room]);

  const handleLeaveRoom = useCallback(async () => {
    try {
      await emitLeaveRoom();
    } catch {
      // Best effort — leave locally regardless
    }
    room.left();
    navigate('/play/multiplayer');
  }, [room, navigate]);

  const handleCopyCode = useCallback(() => {
    if (roomCode) {
      void navigator.clipboard.writeText(roomCode);
    }
  }, [roomCode]);

  return (
    <div className="page room-page">
      <div className="page-bg-suits" aria-hidden="true">
        <SpadeSvg className="page-bg-icon page-bg-1" />
        <HeartSvg className="page-bg-icon page-bg-2" />
        <DiamondSvg className="page-bg-icon page-bg-3" />
        <ClubSvg className="page-bg-icon page-bg-4" />
        <SpadeSvg className="page-bg-icon page-bg-5" />
        <HeartSvg className="page-bg-icon page-bg-6" />
      </div>

      {/* Header */}
      <div className="room-header">
        <button className="btn btn-outline btn-sm" onClick={handleLeaveRoom}>
          ← Leave
        </button>
        <div className="room-code-display">
          <span className="room-code-label">Room Code</span>
          <button
            className="room-code-value"
            onClick={handleCopyCode}
            title="Click to copy"
            aria-label={`Copy room code ${roomCode ?? ''}`}
          >
            {roomCode ?? '—'}
          </button>
        </div>
        <div className="room-status-badge" style={{ color: phaseDisplay.color }}>
          {phaseDisplay.label}
        </div>
      </div>

      {/* Error */}
      {room.error && (
        <div className="auth-error" role="alert">
          {room.error}
          <button className="error-dismiss" onClick={() => room.errorCleared()}>
            ×
          </button>
        </div>
      )}

      {/* Player Slots */}
      <div className="room-players">
        <h2 className="room-section-title">
          Players ({readiness.totalPlayers}/{room.maxPlayers})
        </h2>

        <div className="player-slots">
          {playerInfos.map(({ player, isLocal, canKick }) => (
            <div
              key={player.id}
              className={`player-slot ${isLocal ? 'local' : ''} ${player.isReady ? 'ready' : ''}`}
            >
              <div className="player-slot-avatar">
                {player.isAI ? '🤖' : player.name.charAt(0).toUpperCase()}
              </div>
              <div className="player-slot-info">
                <span className="player-slot-name">
                  {player.name}
                  {isLocal && <span className="you-tag"> (You)</span>}
                  {player.isHost && <span className="host-tag"> ★</span>}
                </span>
                <span className={`player-slot-status ${player.isReady ? 'ready' : 'waiting'}`}>
                  {player.isAI ? 'AI Ready' : player.isReady ? 'Ready' : 'Not Ready'}
                </span>
              </div>
              {canKick && (
                <button
                  className="btn btn-sm player-kick"
                  onClick={() => room.playerRemoved(player.id)}
                  title="Remove player"
                >
                  ✕
                </button>
              )}
            </div>
          ))}

          {/* Empty slots */}
          {Array.from({ length: openSlots }).map((_, i) => (
            <div key={`empty-${i}`} className="player-slot empty">
              <div className="player-slot-avatar empty-avatar">?</div>
              <div className="player-slot-info">
                <span className="player-slot-name">Waiting for player...</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Readiness Bar */}
      <div className="readiness-bar">
        <div className="readiness-track">
          <div
            className="readiness-fill"
            style={{
              width: `${readiness.totalPlayers > 0 ? (readiness.readyPlayers / room.maxPlayers) * 100 : 0}%`,
            }}
          />
        </div>
        <span className="readiness-label">
          {readiness.readyPlayers}/{readiness.totalPlayers} ready
          {readiness.totalPlayers < room.maxPlayers &&
            ` • ${openSlots} slot${openSlots > 1 ? 's' : ''} open`}
        </span>
      </div>

      {/* Actions */}
      <div className="room-actions">
        {/* Toggle ready (non-host and host can both toggle) */}
        <button
          className={`btn btn-lg ${room.players.find((p) => p.id === room.localPlayerId)?.isReady ? 'btn-ready-active' : 'btn-primary'}`}
          onClick={handleToggleReady}
        >
          {room.players.find((p) => p.id === room.localPlayerId)?.isReady ? '✓ Ready!' : 'Ready Up'}
        </button>

        {/* Host controls */}
        {isHost && (
          <>
            {/* Add AI */}
            {canAdd && (
              <div className="add-ai-section">
                {!showAIPanel ? (
                  <button className="btn btn-outline" onClick={() => setShowAIPanel(true)}>
                    + Add AI Player
                  </button>
                ) : (
                  <div className="ai-panel">
                    <div className="ai-difficulty-select">
                      {(['easy', 'medium', 'hard'] as const).map((d) => (
                        <button
                          key={d}
                          className={`btn btn-sm ${aiDifficulty === d ? 'btn-primary' : 'btn-outline'}`}
                          onClick={() => setAiDifficulty(d)}
                        >
                          {d.charAt(0).toUpperCase() + d.slice(1)}
                        </button>
                      ))}
                    </div>
                    <div className="ai-panel-actions">
                      <button className="btn btn-primary btn-sm" onClick={handleAddAI}>
                        Add
                      </button>
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => setShowAIPanel(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Start game */}
            <button
              className="btn btn-accent btn-lg"
              onClick={handleStartGame}
              disabled={!readiness.canStart}
              title={
                !readiness.canStart
                  ? readiness.totalPlayers < room.maxPlayers
                    ? 'Need 4 players to start'
                    : 'All players must be ready'
                  : 'Start the game!'
              }
            >
              {room.phase === 'starting' ? 'Starting...' : 'Start Game'}
            </button>
          </>
        )}
      </div>

      {/* Room Info Footer */}
      <div className="room-info-footer">
        <span>{room.isPrivate ? '🔒 Private Room' : '🌐 Public Room'}</span>
        <span>Host: {room.players.find((p) => p.isHost)?.name ?? user?.username ?? '—'}</span>
        <span>
          {readiness.humanPlayers} human • {readiness.aiPlayers} AI
        </span>
      </div>
    </div>
  );
}
